import {Serializable} from "../../libraries/util/Serializable";
import {Localization} from "../../localization/Localization";
import {Language} from "../../localization/language/Language";
import {LangEnglish} from "../../localization/language/LangEnglish";
import {LangDutch} from "../../localization/language/LangDutch";
import {LocalDate} from "@js-joda/core";

export type ColorThemePreference = "light" | "dark" | "system-default" | "none";

export class Person {
	readonly name: string;
	readonly email: string;
	readonly birthDate: LocalDate;
	
	constructor(name: string, email: string, birthDate: LocalDate) {
		this.name = name;
		this.email = email;
		this.birthDate = birthDate;
	}
	
	copy(values: {
		name?: string;
		email?: string;
		birthDate?: LocalDate;
	}): Person {
		return new Person(
			values.name === undefined ? this.name : values.name,
			values.email === undefined ? this.email : values.email,
			values.birthDate === undefined ? this.birthDate : values.birthDate,
		);
	}
}

export class UserPreferences implements Serializable {
	readonly colorTheme: ColorThemePreference;
	readonly languageKey: string | null;
	readonly datePattern: string | null;
	readonly dateTimePatternShort: string | null;
	readonly dateTimePattern: string | null;
	readonly dateTimePatternLong: string | null;
	readonly inputDateTimePattern: string | null;
	readonly localization: Localization;
	
	constructor(
		colorTheme: ColorThemePreference,
		languageKey: string | null,
		datePattern: string | null,
		dateTimePatternShort: string | null,
		dateTimePattern: string | null,
		dateTimePatternLong: string | null,
		inputDateTimePattern: string | null,
	) {
		this.colorTheme = colorTheme;
		this.languageKey = languageKey;
		this.datePattern = datePattern;
		this.dateTimePatternShort = dateTimePatternShort;
		this.dateTimePattern = dateTimePattern;
		this.dateTimePatternLong = dateTimePatternLong;
		this.inputDateTimePattern = inputDateTimePattern;
		if (languageKey) {
			this.localization = new Localization(UserPreferences._languages[languageKey]);
		} else {
			this.localization = new Localization(UserPreferences.defaultLanguage);
		}
	}
	
	get datePatternOrDefault() {
		return this.datePattern ?? UserPreferences.defaultDatePattern(this.languageKey);
	}
	
	get dateTimePatternShortOrDefault() {
		return this.dateTimePatternShort ?? UserPreferences.defaultDateTimePatternShort(this.languageKey);
	}
	
	get dateTimePatternOrDefault() {
		return this.dateTimePattern ?? UserPreferences.defaultDateTimePattern(this.languageKey);
	}
	
	get dateTimePatternLongOrDefault() {
		return this.dateTimePatternLong ?? UserPreferences.defaultDateTimePatternLong(this.languageKey);
	}
	
	get inputDateTimePatternOrDefault() {
		return this.inputDateTimePattern ?? UserPreferences.defaultInputDateTimePattern(this.languageKey);
	}
	
	static defaultDatePattern(languageKey: string | null) {
		if (languageKey === "nl-NL")
			return "dd/MM/yyyy";
		return "yyyy/MM/dd";
	}
	
	static defaultDateTimePatternShort(languageKey: string | null) {
		if (languageKey === "nl-NL")
			return "dd/MM/yyyy HH:mm '('xxx')'";
		return "yyyy/MM/dd HH:mm '('xxx')'";
	}
	
	static defaultDateTimePattern(languageKey: string | null) {
		if (languageKey === "nl-NL")
			return "dd/MM/yyyy HH:mm:ss '('xxx')'";
		return "yyyy/MM/dd HH:mm:ss '('xxx')'";
	}
	
	static defaultDateTimePatternLong(languageKey: string | null) {
		if (languageKey === "nl-NL")
			return "dd/MM/yyyy HH:mm:ss.SSS '('xxx')'";
		return "yyyy/MM/dd HH:mm:ss.SSS '('xxx')'";
	}
	
	static defaultInputDateTimePattern(languageKey: string | null) {
		if (languageKey === "nl-NL")
			return "dd/MM/yyyy HH:mm:ss";
		return "yyyy/MM/dd HH:mm:ss";
	}
	
	get languageKeyRequired(): string {
		return this.languageKey ?? UserPreferences.defaultLanguage.key;
	}
	
	copy(values: {
		colorTheme?: ColorThemePreference;
		languageKey?: string | null;
		datePattern?: string | null;
		dateTimePatternShort?: string | null;
		dateTimePattern?: string | null;
		dateTimePatternLong?: string | null;
		inputDateTimePattern?: string | null;
	}): UserPreferences {
		return new UserPreferences(
			values.colorTheme === undefined ? this.colorTheme : values.colorTheme,
			values.languageKey === undefined ? this.languageKey : values.languageKey,
			values.datePattern === undefined ? this.datePattern : values.datePattern,
			values.dateTimePatternShort === undefined ? this.dateTimePatternShort : values.dateTimePatternShort,
			values.dateTimePattern === undefined ? this.dateTimePattern : values.dateTimePattern,
			values.dateTimePatternLong === undefined ? this.dateTimePatternLong : values.dateTimePatternLong,
			values.inputDateTimePattern === undefined ? this.inputDateTimePattern : values.inputDateTimePattern,
		);
	}
	
	serialize(): any {
		return {
			colorTheme: this.colorTheme,
			languageKey: this.languageKey,
			datePattern: this.datePattern,
			dateTimePatternShort: this.dateTimePatternShort,
			dateTimePattern: this.dateTimePattern,
			dateTimePatternLong: this.dateTimePatternLong,
			inputDateTimePattern: this.inputDateTimePattern,
		};
	}
	
	static deserialize(json: any): UserPreferences {
		return new UserPreferences(
			json.colorTheme,
			json.languageKey
				// fix old language codes
				?.replace("eng", "en-US")
				.replace("en-uk", "en-US")
				.replace("nld", "nl-NL"),
			json.datePattern,
			json.dateTimePatternShort,
			json.dateTimePattern,
			json.dateTimePatternLong,
			json.inputDateTimePattern,
		);
	}
	
	static supportedLanguages = [LangEnglish, LangDutch];
	static defaultLanguage = LangEnglish;
	
	private static _languages = UserPreferences.supportedLanguages.reduce((r, v) => {
		r[v.key] = v;
		return r;
	}, {} as { [key: string]: Language });
	
	static default: UserPreferences = new UserPreferences(
		"system-default",
		null,
		null,
		null,
		null,
		null,
		null,
	);
}
